import { Button } from "@/components/ui/button";
import { ArrowRight, PlayCircle } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[88vh] flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/33110603/pexels-photo-33110603.jpeg')",
        }}
        aria-hidden
      />
      {/* Side gradient: dark on left, clear on right so cattle stay visible */}
      <div className="absolute inset-0 hero-side-overlay" aria-hidden />

      {/* Rugged Android device showing the Arvo Home Screen — bottom-right overlay (desktop/tablet only) */}
      <img
        src="/screens/hero-device.png?v=5"
        alt="Rugged Android device showing the Arvo Animal Verification screen with a green Verified banner"
        data-testid="hero-device-overlay"
        loading="eager"
        decoding="async"
        className="hidden md:block absolute z-10 pointer-events-none select-none drop-shadow-2xl
                   bottom-4 right-4 w-[22vw] max-w-[260px]
                   lg:bottom-6 lg:right-8 lg:w-[18vw] lg:max-w-[280px]
                   xl:w-[16vw] xl:max-w-[300px]"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36 w-full">
        <div className="max-w-xl lg:max-w-2xl fade-up">
          <h1
            data-testid="hero-headline"
            className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Accurate livestock data capture —{" "}
            <span className="text-[#F2C76A]">fast, simple,</span> and built for
            the yard.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-lg sm:text-xl text-white/90 max-w-xl leading-relaxed"
          >
            LWT, BCS, DNA sampling, herd reads &amp; tag updates — all in one
            mobile tool built for dairy and beef breeding operations.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              asChild
              data-testid="hero-cta-demo"
              className="bg-[#D96C32] hover:bg-[#B85A28] text-white text-base font-bold px-8 py-6 h-auto rounded-md shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <a href="#cta">
                Request a Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-cta-how"
              className="bg-white/10 backdrop-blur-md border-2 border-white/70 text-white hover:bg-white hover:text-[#1A1D1A] text-base font-bold px-8 py-6 h-auto rounded-md transition-all"
            >
              <a href="#how">
                <PlayCircle className="mr-2 w-5 h-5" />
                See How It Works
              </a>
            </Button>
          </div>

          <a
            href="#download"
            data-testid="hero-cta-download"
            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#F2C76A] hover:text-white transition-colors"
          >
            ✨ Now live on the App Store & Google Play — install Arvo today →
          </a>

          {/* Official store badges — links straight to App Store / Google Play */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="https://apps.apple.com/us/app/getarvo/id6762120842"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-badge-app-store"
              aria-label="Download Arvo on the App Store"
              className="inline-block transition-transform hover:-translate-y-0.5"
            >
              <img
                src="/badges/app-store.svg"
                alt="Download on the App Store"
                className="h-14 w-auto block"
                loading="lazy"
                decoding="async"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=nz.getarvo.app"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-badge-play-store"
              aria-label="Get Arvo on Google Play"
              className="inline-block transition-transform hover:-translate-y-0.5"
            >
              <img
                src="/badges/google-play.png?v=2"
                alt="Get it on Google Play"
                className="h-14 w-auto block"
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>

          <div
            data-testid="hero-trust"
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80"
          >
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
              Built for NZ livestock systems
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
              Dairy &amp; Beef ready
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
              Independent &amp; provider-neutral
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
